"use client";

import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { buildPublicationUrl } from "@/lib/publications/urlBuilder";

export default function MagazineComments({ articleId, publicationId, articlePath, siteSettings = {} }) {
  const [comments, setComments] = useState([]);
  const [reader, setReader] = useState(null);
  const [body, setBody] = useState("");
  const [message, setMessage] = useState("");
  const [sessionLoading, setSessionLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const authUrl = route => buildPublicationUrl({ publication: { settings: siteSettings }, path: `/reader/${route}` }) + `?returnTo=${encodeURIComponent(`${articlePath}#comments`)}`;
  const load = useCallback(async () => {
    const response = await fetch(`/api/comments?articleId=${articleId}&publicationId=${publicationId}&sort=desc`);
    if (response.ok) setComments((await response.json()).comments || []);
  }, [articleId, publicationId]);
  useEffect(() => {
    load().catch(() => {});
    let active = true;
    setSessionLoading(true);
    fetch("/api/reader/auth/session").then(response => response.ok ? response.json() : {}).then(data => { if (active) setReader(data.reader || null); }).catch(() => {}).finally(() => { if (active) setSessionLoading(false); });
    return () => { active = false; };
  }, [load]);
  async function submit(event, parentId) {
    event.preventDefault();
    const form = event.currentTarget;
    const value = parentId ? new FormData(event.currentTarget).get("reply") : body;
    if (!String(value || "").trim() || submitting) return;
    setSubmitting(true);
    try {
      const response = await fetch("/api/comments", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ articleId, publicationId, body: value, ...(parentId ? { parentId } : {}) }) });
      const data = await response.json();
      setMessage(response.ok ? data.message : data.error);
      if (response.status === 401) setReader(null);
      if (response.ok) { setBody(""); form.reset(); await load(); }
    } catch { setMessage("Your comment could not be sent. Please try again."); }
    finally { setSubmitting(false); }
  }
  async function react(endpoint, payload) {
    const response = await fetch(endpoint, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (response.ok) await load();
  }
  return <section className="mag-comments" id="comments">
    <h2 className="mag-heading">{reader ? "Join the conversation" : "Want to join the conversation?"}</h2>
    {sessionLoading ? <p role="status">Checking sign-in…</p> : !reader ? <div className="mag-comment-auth"><p>Sign in or create an account to comment.</p><div className="mag-comment-actions"><Link className="mag-button" href={authUrl("login")}>Sign In</Link><Link className="mag-comment-register" href={authUrl("register")}>Create Account</Link></div></div> : <form className="mag-comment-form" onSubmit={event => submit(event)}><label htmlFor="mag-comment-body">Add a comment</label><textarea id="mag-comment-body" value={body} onChange={event => setBody(event.target.value)} maxLength={2000} required /><button className="mag-button" disabled={submitting}>{submitting ? "Submitting…" : "Post comment"}</button></form>}
    {message && <p role="status">{message}</p>}
    {comments.map(comment => <article className="mag-comment" key={comment.id}>
      <strong>{comment.reader?.displayName || "Reader"}</strong><p>{comment.body}</p>
      {reader && <div><button className="mag-icon-link" onClick={() => react("/api/comments/like", { commentId: comment.id })}>Like {comment.likeCount || ""}</button><button className="mag-icon-link" onClick={() => { const reason = window.prompt("Why are you reporting this comment?"); if (reason) react("/api/comments/report", { commentId: comment.id, reason }); }}>Report</button><form className="mag-comment-form" onSubmit={event => submit(event, comment.id)}><input name="reply" aria-label={`Reply to ${comment.reader?.displayName || "comment"}`} placeholder="Write a reply" required /><button className="mag-button">Reply</button></form></div>}
      {comment.replies?.map(reply => <div className="mag-comment" key={reply.id}><strong>{reply.reader?.displayName || "Reader"}</strong><p>{reply.body}</p></div>)}
    </article>)}
  </section>;
}
